// Category switcher: primary product-view navigation across the five
// data blobs (frontend/DATA_CONTRACT.md).  flower ships in the boot
// blob; the rest load on demand via <script src="/data/<key>.js"> and
// register themselves under window.WEEDBUD_BLOBS[key].

import React, { useEffect, useState } from "react";
import type { CategoryBlob } from "./types";
import { BrandIcon } from "./icons";

// localStorage key for the last-active category. Restored on boot so a
// refresh returns to the tab you were on -- and, crucially, so the
// persisted filters (wb.filters.v1, keyed to that category) match the
// category they were set for. Booting to flower while restoring, say, an
// edible-only filter would drop every flower row ("No products match").
const CAT_KEY = "wb.cat.v1";

export interface CategoryTab {
  key: string;
  label: string;
}

// Left-to-right by decreasing product count (flower > vape > extract >
// edible > other), not backend/categories.py's BLOB_KEYS order -- that order
// is fetch/parse/write plumbing order, unrelated to catalog size.
export const CATEGORY_TABS: CategoryTab[] = [
  { key: "flower", label: "Flower" },
  { key: "vape", label: "Vapes" },
  { key: "extract", label: "Concentrates" },
  { key: "edible", label: "Edibles" },
  { key: "other", label: "Other" },
];

export type BlobStatus = "loading" | "ready" | "error";

// The persisted category, validated against the known tabs (a stale/bogus
// value falls back to flower). flower is always safe -- it ships in the
// boot blob and needs no on-demand load.
function initialCategory(): string {
  try {
    const c = localStorage.getItem(CAT_KEY);
    if (c && CATEGORY_TABS.some((t) => t.key === c)) return c;
  } catch {
    /* localStorage unavailable */
  }
  return "flower";
}

export interface UseCategoryBlobs {
  active: string;
  select: (key: string) => void;
  status: Record<string, BlobStatus>;
  activeBlob: CategoryBlob | null;
  retry: () => void;
}

// Registers the boot blob under WEEDBUD_BLOBS.flower so every category
// -- including flower -- can be looked up the same way.  Idempotent:
// a second mount (e.g. React StrictMode) is a harmless no-op.
function registerBootBlob() {
  const D = window.WEEDBUD_DATA;
  if (!D) return;
  window.WEEDBUD_BLOBS = window.WEEDBUD_BLOBS || {};
  if (window.WEEDBUD_BLOBS.flower) return;
  window.WEEDBUD_BLOBS.flower = {
    blob: "flower",
    as_of: D.as_of,
    date: D.date,
    rows: D.rows,
    brands: D.brands,
    categories: D.categories,
    strain_types: D.strain_types,
    terpene_names: D.terpene_names,
    oils: D.oils ?? [],
    formats: D.formats ?? [],
    weights: D.weights ?? [],
    has_solventless: D.has_solventless ?? false,
    concentrate_forms: D.concentrate_forms ?? [],
    edible_forms: D.edible_forms ?? [],
    doses: D.doses ?? [],
    has_infused: D.has_infused ?? false,
    flower_forms: D.flower_forms ?? [],
    other_forms: D.other_forms ?? [],
  };
}

export function useCategoryBlobs(): UseCategoryBlobs {
  const [active, setActive] = useState(initialCategory);
  const [status, setStatus] = useState<Record<string, BlobStatus>>({ flower: "ready" });
  // Blob payloads live on window.WEEDBUD_BLOBS, not React state (they're
  // large and never mutate once loaded); bump this to force a re-render
  // that re-reads the window global.
  const [, setBlobVersion] = useState(0);

  // Registering the boot blob must happen before the first read of
  // activeBlob below, not in a useEffect: an effect fires after the first
  // commit, and mutating window.WEEDBUD_BLOBS there doesn't itself trigger
  // a re-render, so activeBlob would stay null forever. registerBootBlob
  // is idempotent, so calling it inline on every render is safe.
  registerBootBlob();

  const load = (key: string) => {
    setStatus((s) => ({ ...s, [key]: "loading" }));
    const script = document.createElement("script");
    script.src = `/data/${key}.js`;
    script.onload = () => {
      script.remove();
      if (window.WEEDBUD_BLOBS?.[key]) {
        setStatus((s) => ({ ...s, [key]: "ready" }));
        setBlobVersion((v) => v + 1);
      } else {
        setStatus((s) => ({ ...s, [key]: "error" }));
      }
    };
    script.onerror = () => {
      script.remove();
      setStatus((s) => ({ ...s, [key]: "error" }));
    };
    document.body.appendChild(script);
  };

  const select = (key: string) => {
    setActive(key);
    const st = status[key];
    if (st !== "ready" && st !== "loading") load(key);
  };

  // Boot: a restored non-flower category has no blob yet (only flower ships
  // in the boot HTML), so kick off its on-demand load. `select` handles
  // every later change; this covers the initial value that skipped it.
  useEffect(() => {
    if (active !== "flower" && status[active] !== "ready" && status[active] !== "loading") {
      // Kicking off the on-demand fetch is the point of this mount effect;
      // the loading-status update is that fetch reflected into React state.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      load(active);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the active category (see CAT_KEY) so the next boot restores it.
  useEffect(() => {
    try {
      localStorage.setItem(CAT_KEY, active);
    } catch {
      /* localStorage unavailable */
    }
  }, [active]);

  const retry = () => load(active);

  const activeBlob = window.WEEDBUD_BLOBS?.[active] ?? null;

  return { active, select, status, activeBlob, retry };
}

export function CategorySwitcher({
  active,
  select,
  status,
  counts,
}: {
  active: string;
  select: (key: string) => void;
  status: Record<string, BlobStatus>;
  counts: Record<string, number>;
}) {
  return (
    <nav className="cat-switcher">
      {CATEGORY_TABS.map((t) => (
        <button
          key={t.key}
          className={"cat-tab" + (t.key === active ? " is-on" : "")}
          onClick={() => select(t.key)}
        >
          <span>{t.label}</span>
          <span className={"cat-tab-count mono" + (status[t.key] === "error" ? " is-error" : "")}>
            {(counts[t.key] ?? 0).toLocaleString()}
          </span>
        </button>
      ))}
    </nav>
  );
}

export function BlobLoading({ label }: { label: string }) {
  return (
    <div className="blob-state">
      <div className="blob-logo">
        <BrandIcon />
      </div>
      <div className="blob-state-msg mono">Refreshing {label}</div>
    </div>
  );
}

export function BlobError({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <div className="blob-state">
      <div className="blob-state-icon">!</div>
      <div className="blob-state-title">Could not load {label}</div>
      <div className="blob-state-msg mono">
        This category may not have data yet — this is expected before its first push.
      </div>
      <button className="btn" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

// "2:00 PM" in the viewer's local time zone.
export function formatAsOf(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
